import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Displays a friendly hint when the user types an e-mail that's registered
 * in the *other* portal (the one they're not currently on).
 *
 * Props:
 *   email      : current email value
 *   expected   : 'medewerker' | 'klant' — what role THIS login page expects
 */
const WrongPortalHint = ({ email, expected }) => {
  const [hint, setHint] = useState(null); // 'wrong-portal-klant' | 'wrong-portal-medewerker' | null
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    setHint(null);
    if (!email || !email.includes('@') || email.length < 5) return;
    timer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/detect-role`, { params: { email } });
        const role = res.data?.role;
        if (!role) return;
        if (role !== expected) {
          setHint(role); // user is on wrong portal
        }
      } catch (_) {
        // silent — feature is optional
      }
    }, 600);
    return () => timer.current && clearTimeout(timer.current);
  }, [email, expected]);

  if (!hint) return null;

  const goesTo = hint === 'klant'
    ? { url: `/client?email=${encodeURIComponent(email)}`, label: 'Klantenportaal', message: 'Dit e-mailadres is een Klant-account.' }
    : { url: `/login?email=${encodeURIComponent(email)}`, label: 'Medewerker login', message: 'Dit e-mailadres is een Medewerker-account.' };

  return (
    <Link
      to={goesTo.url}
      className="flex items-center justify-between bg-[#3da5a5]/10 border border-[#3da5a5]/40 text-white rounded-lg p-3 hover:bg-[#3da5a5]/20 transition"
      data-testid="wrong-portal-hint"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-[#3da5a5] mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-semibold">{goesTo.message}</p>
          <p className="text-white/70 text-xs">Tik om naar het {goesTo.label} te gaan.</p>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-[#3da5a5]" />
    </Link>
  );
};

export default WrongPortalHint;
